import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, RefreshCw, Save, Printer, Plus, Minus, History, Truck, Send, Image as ImageIcon, X as XIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Barcode from "../components/Barcode";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const empty = {
  name: "",
  description: "",
  ean: "",
  quantity: 0,
  quantity_buffer: 0,
  quantity_pickup: 0,
  min_quantity: 0,
  ek_price: 0,
  vk_price: 0,
  shelf_label: "",
  compartment: "",
  slot: "",
  location_text: "",
  supplier_id: null,
  supplier_sku: "",
  reorder_qty: 0,
  reorder_lead_days: 0,
  group_id: null,
  image_base64: "",
  unit: "Stk",
  misc: "",
  is_set: false,
  set_components: [],
};

const UNITS = ["Stk", "Paar", "Set", "Satz", "Meter", "kg", "Liter"];

const NONE = "__none__";

function compartmentsOf(shelf) {
  if (!shelf) return [];
  const total = (shelf.rows || 1) * (shelf.cols || 1);
  const labels = Array.isArray(shelf.compartment_labels) ? shelf.compartment_labels : [];
  return Array.from({ length: total }, (_, i) => {
    const custom = (labels[i] || "").trim();
    return custom || `F${String(i + 1).padStart(2, "0")}`;
  });
}

export default function ArticleEdit() {
  const { id } = useParams();
  const isNew = !id || id === "neu";
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [shelves, setShelves] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [movements, setMovements] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [qbAmount, setQbAmount] = useState(1);
  const [allArticles, setAllArticles] = useState([]);
  const [assembleCount, setAssembleCount] = useState(1);
  const [assembling, setAssembling] = useState(false);

  useEffect(() => {
    api.get("/shelves").then((r) => setShelves(r.data || [])).catch(() => setShelves([]));
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
    api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
    api.get("/articles").then((r) => setAllArticles(r.data || [])).catch(() => setAllArticles([]));
  }, []);

  // Image upload (compress + base64)
  const onImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild ist größer als 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        // Resize to max 800px on longer side, JPEG q 0.82
        const maxSide = 800;
        let { width, height } = img;
        if (width > height && width > maxSide) {
          height = Math.round((height * maxSide) / width);
          width = maxSide;
        } else if (height > maxSide) {
          width = Math.round((width * maxSide) / height);
          height = maxSide;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        setForm((s) => ({ ...s, image_base64: dataUrl }));
        toast.success("Bild hochgeladen.");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearImage = () => setForm((s) => ({ ...s, image_base64: "" }));

  // ----- Set components -----
  const FREE = "__free__";
  const addComponent = (articleId) => {
    setForm((s) => {
      const comps = [...(s.set_components || [])];
      if (articleId === FREE) {
        comps.push({ article_id: null, name: "", qty: 1 });
      } else {
        const art = allArticles.find((x) => x.id === articleId);
        comps.push({ article_id: articleId, name: art?.name || "", qty: 1 });
      }
      return { ...s, set_components: comps };
    });
  };
  const updateComponent = (idx, patch) => {
    setForm((s) => {
      const comps = [...(s.set_components || [])];
      comps[idx] = { ...comps[idx], ...patch };
      return { ...s, set_components: comps };
    });
  };
  const removeComponent = (idx) => {
    setForm((s) => ({
      ...s,
      set_components: (s.set_components || []).filter((_, i) => i !== idx),
    }));
  };

  const assemble = async () => {
    setAssembling(true);
    try {
      const { data } = await api.post(`/articles/${id}/assemble`, { count: assembleCount });
      setForm(data.article);
      toast.success(`${data.assembled}× Set zusammengestellt — Einzelteile wurden abgebucht.`);
      reloadMovements();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAssembling(false);
    }
  };

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/articles/${id}`)
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get(`/movements?article_id=${id}&limit=20`).then((r) => setMovements(r.data));
  }, [id, isNew]);

  const reloadMovements = () =>
    api.get(`/movements?article_id=${id}&limit=20`).then((r) => setMovements(r.data));

  const selectedShelf = useMemo(
    () => shelves.find((s) => s.label.toUpperCase() === (form.shelf_label || "").toUpperCase()),
    [shelves, form.shelf_label]
  );
  const compartments = useMemo(() => compartmentsOf(selectedShelf), [selectedShelf]);

  const onChange = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };
  const onChangeNum = (k) => (e) => {
    setForm((s) => ({ ...s, [k]: e.target.value === "" ? 0 : Number(e.target.value) }));
  };

  const setShelf = (label) => {
    if (label === NONE) {
      setForm((s) => ({ ...s, shelf_label: "", compartment: "", slot: "" }));
    } else {
      setForm((s) => ({ ...s, shelf_label: label, compartment: "", slot: "" }));
    }
  };
  const setCompartment = (label) => {
    setForm((s) => ({ ...s, compartment: label === NONE ? "" : label }));
  };

  const generate = async () => {
    try {
      const { data } = await api.get("/generate-ean");
      setForm((s) => ({ ...s, ean: data.ean }));
      toast.success("EAN-13 generiert.");
    } catch (e) {
      toast.error("Konnte EAN nicht generieren.");
    }
  };

  // For existing articles with an EAN: confirm before regenerating
  const onGenerateClick = () => {
    if (!isNew && form.ean) {
      setConfirmRegen(true);
    } else {
      generate();
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/articles", form);
        toast.success("Artikel angelegt.");
        navigate(`/artikel/${data.id}`);
      } else {
        await api.put(`/articles/${id}`, form);
        toast.success("Gespeichert.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const adjust = async (delta) => {
    if (isNew) return;
    const isOut = delta < 0;
    let project = "";
    if (isOut) {
      project = window.prompt("Projekt für die Entnahme angeben (Hauptlager):") || "";
      if (!project.trim()) {
        toast.error("Entnahme abgebrochen — Projekt ist Pflicht.");
        return;
      }
    }
    try {
      const { data } = await api.post(`/articles/${id}/adjust`, {
        delta,
        project: project.trim(),
        type: isOut ? "entnahme" : "zugang",
        location: "main",
      });
      setForm(data);
      reloadMovements();
      toast.success(delta > 0 ? `+${delta} Hauptlager` : `${delta} Hauptlager`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendReorder = async () => {
    if (!form.supplier_id) {
      toast.error("Erst Lieferant zuordnen und speichern.");
      return;
    }
    const qty = window.prompt(
      `Bestellmenge für „${form.name}" (leer = Vorschlag):`,
      String(form.reorder_qty || "")
    );
    if (qty === null) return;
    setReordering(true);
    try {
      const payload = qty.trim() ? { qty: Number(qty) } : {};
      const { data } = await api.post(`/articles/${id}/reorder`, payload);
      toast.success(`Bestellung ${data.order_no} versendet — ${data.qty} Stk.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReordering(false);
    }
  };

  if (loading) return <div className="text-zinc-500 mono text-sm">LADEN…</div>;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="article-edit-page">
      <div className="flex items-center justify-between">
        <Link to="/artikel" className="inline-flex items-center text-sm text-zinc-600 hover:text-black">
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
        </Link>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500">
          {isNew ? "NEUER ARTIKEL" : `ID · ${id?.slice(0, 8)}`}
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-3xl sm:text-4xl uppercase break-words">
          {isNew ? "Artikel anlegen" : form.name || "Artikel bearbeiten"}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && form.ean && (
            <Link
              to={`/etiketten?article=${id}`}
              className="inline-flex items-center gap-2 bg-white border border-zinc-400 hover:bg-zinc-50 px-4 h-11 rounded-sm text-sm font-medium"
            >
              <Printer className="w-4 h-4" /> Etikett drucken
            </Link>
          )}
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-article-button"
            className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-5 font-semibold tracking-wide"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "SPEICHERN…" : "SPEICHERN"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-5 bg-white border border-zinc-200 rounded-sm p-5 sm:p-6">
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">STAMMDATEN</div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
            <Input
              value={form.name}
              onChange={onChange("name")}
              data-testid="article-name-input"
              className="mt-1.5 rounded-sm border-zinc-400"
              placeholder="z.B. Schraube M6 x 30"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Beschreibung</Label>
            <Textarea
              value={form.description}
              onChange={onChange("description")}
              data-testid="article-description-input"
              className="mt-1.5 rounded-sm border-zinc-400 min-h-[80px]"
              placeholder="Material, Farbe, Hinweise…"
            />
          </div>

          {/* Group / Unit / Lead time / Image */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Gruppe</Label>
              <Select
                value={form.group_id || NONE}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, group_id: v === NONE ? null : v }))
                }
              >
                <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="article-group-select">
                  <SelectValue placeholder="Gruppe wählen" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value={NONE}>— ohne Gruppe —</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-sm"
                          style={{ backgroundColor: g.color }}
                        />
                        {g.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Einheit</Label>
              <Select
                value={form.unit || "Stk"}
                onValueChange={(v) => setForm((s) => ({ ...s, unit: v }))}
              >
                <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="article-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Sonstiges</Label>
              <Input
                value={form.misc || ""}
                onChange={onChange("misc")}
                data-testid="article-misc-input"
                className="mt-1.5 rounded-sm border-zinc-400"
                placeholder="z.B. Hinweise, Zubehör…"
              />
            </div>
          </div>

          {/* Set / Stückliste */}
          <div className="border border-zinc-300 rounded-sm p-4 bg-zinc-50/50">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Ist ein Set</Label>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Ein Set enthält mehrere Einzelartikel oder Freitext-Positionen.
                </p>
              </div>
              <Switch
                checked={!!form.is_set}
                onCheckedChange={(v) => setForm((s) => ({ ...s, is_set: v }))}
                data-testid="article-isset-switch"
              />
            </div>
            {form.is_set && (
              <div className="mt-4 space-y-2" data-testid="set-components-editor">
                {(form.set_components || []).map((c, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span
                      className={`shrink-0 mono text-[9px] tracking-wider px-1.5 py-1 rounded-sm ${
                        c.article_id ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-600"
                      }`}
                    >
                      {c.article_id ? "ARTIKEL" : "FREITEXT"}
                    </span>
                    {c.article_id ? (
                      <div className="flex-1 text-sm font-medium truncate" data-testid={`set-comp-name-${idx}`}>
                        {allArticles.find((x) => x.id === c.article_id)?.name || c.name}
                      </div>
                    ) : (
                      <Input
                        value={c.name || ""}
                        onChange={(e) => updateComponent(idx, { name: e.target.value })}
                        placeholder="z.B. Kleinteile-Tüte"
                        data-testid={`set-comp-freetext-${idx}`}
                        className="flex-1 rounded-sm border-zinc-400 h-9"
                      />
                    )}
                    <Input
                      type="number"
                      min={1}
                      value={c.qty}
                      onChange={(e) =>
                        updateComponent(idx, { qty: Math.max(1, Number(e.target.value) || 1) })
                      }
                      data-testid={`set-comp-qty-${idx}`}
                      className="w-20 rounded-sm border-zinc-400 h-9 font-mono-num text-center"
                    />
                    <button
                      onClick={() => removeComponent(idx)}
                      data-testid={`set-comp-remove-${idx}`}
                      className="shrink-0 w-9 h-9 rounded-sm border border-zinc-300 hover:bg-red-50 hover:border-red-300 flex items-center justify-center text-zinc-500 hover:text-red-600"
                      title="Position entfernen"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <Select value="" onValueChange={addComponent}>
                    <SelectTrigger
                      className="rounded-sm border-zinc-400 h-9 flex-1"
                      data-testid="set-add-component-select"
                    >
                      <SelectValue placeholder="+ Inhalt hinzufügen (Artikel oder Freitext)…" />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm max-h-64">
                      <SelectItem value="__free__">✏️ Freitext-Position</SelectItem>
                      {allArticles
                        .filter((x) => x.id !== id)
                        .map((x) => (
                          <SelectItem key={x.id} value={x.id}>
                            {x.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {(form.set_components || []).length > 0 && (
                  <div className="text-[11px] text-zinc-500">
                    {(form.set_components || []).reduce((s, c) => s + (Number(c.qty) || 0), 0)} Teile
                    in diesem Set · verknüpfte Artikel werden bei „Set zusammenstellen" abgebucht.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Image upload */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Foto</Label>
            <div className="mt-1.5 flex items-start gap-4">
              {form.image_base64 ? (
                <div className="relative w-32 h-32 border border-zinc-300 rounded-sm overflow-hidden bg-zinc-50 shrink-0">
                  <img
                    src={form.image_base64}
                    alt="Artikel"
                    className="w-full h-full object-contain"
                    data-testid="article-image-preview"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    data-testid="article-image-remove"
                    className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-sm p-1"
                    title="Bild entfernen"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 border-2 border-dashed border-zinc-300 rounded-sm flex items-center justify-center bg-zinc-50 shrink-0">
                  <ImageIcon className="w-8 h-8 text-zinc-400" strokeWidth={1.5} />
                </div>
              )}
              <div className="flex-1">
                <label
                  className="inline-flex items-center bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-sm text-xs font-semibold tracking-wide cursor-pointer"
                  data-testid="article-image-upload-label"
                >
                  <ImageIcon className="w-4 h-4 mr-2" /> Bild hochladen
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onImageUpload}
                    className="hidden"
                    data-testid="article-image-upload-input"
                  />
                </label>
                <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                  Max. 5 MB · wird automatisch auf 800 px komprimiert und im Artikel gespeichert.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Hauptlager</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={onChangeNum("quantity")}
                data-testid="article-quantity-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Zwischenlager</Label>
              <Input
                type="number"
                value={form.quantity_buffer}
                onChange={onChangeNum("quantity_buffer")}
                data-testid="article-quantity-buffer-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Abhollager</Label>
              <Input
                type="number"
                value={form.quantity_pickup}
                onChange={onChangeNum("quantity_pickup")}
                data-testid="article-quantity-pickup-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
            <div className="col-span-3 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Mindestmenge (Hauptlager)</Label>
                <Input
                  type="number"
                  value={form.min_quantity}
                  onChange={onChangeNum("min_quantity")}
                  data-testid="article-min-quantity-input"
                  className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  E-Mail bei Erreichen dieser Menge im Hauptlager. Standard 0 = bei leerem Hauptlager.
                </div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 self-end">
                <div className="mono text-[10px] tracking-wider text-zinc-500">GESAMT</div>
                <div className="font-display text-3xl font-mono-num">
                  {(Number(form.quantity) || 0) + (Number(form.quantity_buffer) || 0) + (Number(form.quantity_pickup) || 0)}
                </div>
                <div className="text-[11px] text-zinc-500">Stk. über alle Lager</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">EK-Preis (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.ek_price}
                onChange={onChangeNum("ek_price")}
                data-testid="article-ek-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">VK-Preis (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.vk_price}
                onChange={onChangeNum("vk_price")}
                data-testid="article-vk-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-zinc-200 mono text-[10px] tracking-[0.25em] text-zinc-500">
            LAGERPLATZ
          </div>
          {shelves.length === 0 ? (
            <div className="text-sm bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
              Noch keine Regale angelegt.{" "}
              <Link to="/lagerplan" className="text-[#F97316] font-medium underline">
                Im Lagerplan erstellen →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Regal</Label>
                <Select value={form.shelf_label || NONE} onValueChange={setShelf}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 font-mono-num" data-testid="article-shelf-select">
                    <SelectValue placeholder="Regal wählen" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm max-h-64">
                    <SelectItem value={NONE}>— kein Regal —</SelectItem>
                    {shelves.map((s) => (
                      <SelectItem key={s.id} value={s.label}>
                        {s.label}
                        {s.name ? ` · ${s.name}` : ""}
                        {s.is_return_shelf ? " · Rückbuchung" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Fach</Label>
                <Select
                  value={form.compartment || NONE}
                  onValueChange={setCompartment}
                  disabled={!selectedShelf}
                >
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 font-mono-num" data-testid="article-compartment-select">
                    <SelectValue placeholder={selectedShelf ? "Fach wählen" : "Erst Regal wählen"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm max-h-64">
                    <SelectItem value={NONE}>— kein Fach —</SelectItem>
                    {compartments.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Platz</Label>
                <Input
                  value={form.slot}
                  onChange={onChange("slot")}
                  data-testid="article-slot-input"
                  placeholder="z.B. P02"
                  className="mt-1.5 rounded-sm border-zinc-400 font-mono-num uppercase"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Beschriftung (Etikett)</Label>
            <Input
              value={form.location_text}
              onChange={onChange("location_text")}
              data-testid="article-location-text-input"
              placeholder="z.B. Schublade oder Feder L703"
              className="mt-1.5 rounded-sm border-zinc-400"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              Erscheint auf dem gedruckten Etikett oberhalb des EAN-Codes.
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-zinc-200 mono text-[10px] tracking-[0.25em] text-zinc-500">
            LIEFERANT &amp; NACHBESTELLUNG
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Lieferant</Label>
              <Select
                value={form.supplier_id || NONE}
                onValueChange={(v) => setForm((s) => ({ ...s, supplier_id: v === NONE ? null : v }))}
              >
                <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="article-supplier-select">
                  <SelectValue placeholder="Lieferant wählen" />
                </SelectTrigger>
                <SelectContent className="rounded-sm max-h-64">
                  <SelectItem value={NONE}>— kein Lieferant —</SelectItem>
                  {suppliers.map((sp) => (
                    <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suppliers.length === 0 && (
                <div className="text-[11px] text-zinc-500 mt-1">
                  <Link to="/lieferanten" className="text-[#F97316] underline">Lieferanten anlegen →</Link>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Artikel-Nr. beim Lieferant</Label>
              <Input
                value={form.supplier_sku || ""}
                onChange={onChange("supplier_sku")}
                data-testid="article-supplier-sku-input"
                placeholder="z.B. L703-G"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Bestellmenge (Vorschlag)</Label>
              <Input
                type="number"
                value={form.reorder_qty || 0}
                onChange={onChangeNum("reorder_qty")}
                data-testid="article-reorder-qty-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Standard-Bestellmenge. Leer = automatischer Vorschlag.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Lieferzeit (Tage)</Label>
              <Input
                type="number"
                value={form.reorder_lead_days || 0}
                onChange={onChangeNum("reorder_lead_days")}
                data-testid="article-lead-days-input"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
              />
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">EAN-13</div>
              <button
                onClick={onGenerateClick}
                data-testid="generate-ean-button"
                className="inline-flex items-center gap-1.5 text-xs hover:text-[#F97316]"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Generieren
              </button>
            </div>
            <Input
              value={form.ean || ""}
              onChange={onChange("ean")}
              placeholder="leer = automatisch beim Speichern"
              data-testid="article-ean-input"
              className="rounded-sm border-zinc-400 font-mono-num"
            />
            <div className="mt-4 border border-dashed border-zinc-300 p-3 rounded-sm bg-zinc-50 flex items-center justify-center min-h-[110px]">
              {form.ean ? (
                <Barcode value={form.ean} height={60} />
              ) : (
                <div className="text-xs text-zinc-400 mono">VORSCHAU NACH EINGABE</div>
              )}
            </div>
          </div>

          {!isNew && form.supplier_id && (
            <div className="bg-white border border-zinc-200 rounded-sm p-5" style={{ borderLeft: "4px solid #0EA5E9" }}>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">NACHBESTELLUNG</div>
              <div className="text-sm mb-3">
                Lieferant: <span className="font-semibold">{suppliers.find((sp) => sp.id === form.supplier_id)?.name || "—"}</span>
              </div>
              <Button
                onClick={sendReorder}
                disabled={reordering}
                data-testid="reorder-button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-11 font-semibold tracking-wide"
              >
                <Send className="w-4 h-4 mr-2" />
                {reordering ? "VERSENDEN…" : "BESTELLUNG SENDEN"}
              </Button>
              <div className="text-[11px] text-zinc-500 mt-2">
                Versendet PDF-Bestellung per E-Mail an den Lieferanten.
              </div>
            </div>
          )}

          {!isNew && (
            <div className="bg-white border border-zinc-200 rounded-sm p-5" style={{ borderLeft: "4px solid #F97316" }}>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">SCHNELL-BUCHUNG</div>
              <div className="font-display text-4xl font-mono-num mb-4" data-testid="current-qty">
                {form.quantity}
              </div>
              <div className="flex items-stretch gap-2 mb-3">
                <button
                  onClick={() => setQbAmount((a) => Math.max(1, a - 1))}
                  data-testid="qb-minus"
                  className="w-12 h-12 shrink-0 rounded-sm border border-zinc-400 hover:bg-zinc-50 flex items-center justify-center"
                  aria-label="Menge verringern"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <Input
                  type="number"
                  min={1}
                  value={qbAmount}
                  onChange={(e) => setQbAmount(Math.max(1, Number(e.target.value) || 1))}
                  data-testid="qb-amount-input"
                  className="rounded-sm border-zinc-400 font-mono-num text-2xl h-12 text-center flex-1"
                />
                <button
                  onClick={() => setQbAmount((a) => a + 1)}
                  data-testid="qb-plus"
                  className="w-12 h-12 shrink-0 rounded-sm border border-zinc-400 hover:bg-zinc-50 flex items-center justify-center"
                  aria-label="Menge erhöhen"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {[5, 10, 25].map((n) => (
                  <button
                    key={n}
                    onClick={() => setQbAmount(n)}
                    data-testid={`qb-preset-${n}`}
                    className={`h-8 rounded-sm text-xs font-semibold border transition-colors ${
                      qbAmount === n
                        ? "bg-black border-black text-white"
                        : "border-zinc-300 hover:bg-zinc-50 text-zinc-600"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => adjust(qbAmount)}
                  data-testid="quick-add-btn"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-11 font-semibold"
                >
                  <Plus className="w-4 h-4 mr-1" /> +{qbAmount}
                </Button>
                <Button
                  onClick={() => adjust(-qbAmount)}
                  data-testid="quick-remove-btn"
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-11 font-semibold"
                >
                  <Minus className="w-4 h-4 mr-1" /> −{qbAmount}
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Bei Entnahme wird ein Projektname abgefragt (Pflicht).
              </div>
            </div>
          )}

          {!isNew && form.is_set && (
            <div className="bg-white border border-zinc-200 rounded-sm p-5" style={{ borderLeft: "4px solid #18181b" }}>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">SET ZUSAMMENSTELLEN</div>
              <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
                Bucht die Einzelteile vom Hauptlager ab und erhöht den Set-Bestand.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  value={assembleCount}
                  onChange={(e) => setAssembleCount(Math.max(1, Number(e.target.value) || 1))}
                  data-testid="assemble-count-input"
                  className="w-20 rounded-sm border-zinc-400 h-11 font-mono-num text-center"
                />
                <Button
                  onClick={assemble}
                  disabled={assembling || (form.set_components || []).filter((c) => c.article_id).length === 0}
                  data-testid="assemble-button"
                  className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 font-semibold"
                >
                  {assembling ? "BUCHE…" : `${assembleCount}× ZUSAMMENSTELLEN`}
                </Button>
              </div>
              {(form.set_components || []).filter((c) => c.article_id).length === 0 && (
                <div className="text-[11px] text-amber-600 mt-2">
                  Keine verknüpften Lager-Artikel im Set — nichts zum Abbuchen.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History per article */}
      {!isNew && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <History className="w-4 h-4" />
              <div>
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">BEWEGUNGEN</div>
                <div className="font-display text-lg uppercase">Historie ({movements.length})</div>
              </div>
            </div>
            <Link to={`/historie?article=${id}`} className="text-xs text-[#F97316] font-medium hover:underline">
              Alles anzeigen →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-zinc-600">
                <tr>
                  <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">ZEITPUNKT</th>
                  <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">TYP</th>
                  <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">MENGE</th>
                  <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">PROJEKT</th>
                  <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">BENUTZER</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-zinc-500 text-sm">
                      Noch keine Bewegungen.
                    </td>
                  </tr>
                ) : (
                  movements.map((m) => (
                    <tr key={m.id} className="border-t border-zinc-100">
                      <td className="px-4 py-2 mono text-xs">
                        {new Date(m.at).toLocaleString("de-DE")}
                      </td>
                      <td className="px-4 py-2">
                        <MovementType type={m.type} delta={m.delta} />
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-mono-num font-semibold ${
                          m.delta > 0 ? "text-green-700" : "text-red-700"
                        }`}
                      >
                        {m.delta > 0 ? `+${m.delta}` : m.delta}
                      </td>
                      <td className="px-4 py-2 text-xs">{m.project || "—"}</td>
                      <td className="px-4 py-2 mono text-xs text-zinc-500">{m.user_email}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Code wirklich neu generieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Artikel hat bereits den Code{" "}
              <span className="font-mono font-semibold text-zinc-900">{form.ean}</span>.
              Wenn du einen neuen Code generierst, ist der alte Code nicht mehr aktiv —
              bereits gedruckte Etiketten funktionieren dann beim Scannen nicht mehr.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="confirm-regen-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-regen-confirm"
              onClick={() => {
                setConfirmRegen(false);
                generate();
              }}
            >
              Ja, neu generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MovementType({ type, delta }) {
  const map = {
    entnahme: { label: "Entnahme", cls: "bg-red-100 text-red-700" },
    zugang: { label: "Zugang", cls: "bg-green-100 text-green-700" },
    rueckbuchung: { label: "Rückbuchung", cls: "bg-blue-100 text-blue-700" },
  };
  const fallback = delta < 0 ? map.entnahme : map.zugang;
  const t = map[type] || fallback;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm mono text-[10px] uppercase tracking-wider ${t.cls}`}>
      {t.label}
    </span>
  );
}
